import {AfterViewInit, Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import { SectionBase } from '../../../components/section/section-base';
import { VehicleDataEnum } from '../../../../core/consts/vehicle-data.enum';
import { InsuranceDataEnum } from '../../../../core/consts/insurance-data.enum';
import { VehicleStateEnum } from '../../../../core/consts/vehicle-state.enum';
import { VehicleModelConsts } from '../../../../core/consts/vehicle-model.consts';
import { VehicleCondition } from '../../../../core/consts/vehicle-condition.enum';
import {
  VehicleDataStateService
} from '../../../../core/services/impl/section-services/vehicle-data/vehicle-data-state.service';
import {
  PredefinedValuesService
} from '../../../../core/services/impl/section-services/predefined-values/predefined-values.service';
import {
  PredefinedValuesStateService
} from '../../../../core/services/impl/section-services/predefined-values/predefined-values-state.service';
import {
  InsuranceDataStateService
} from '../../../../core/services/impl/section-services/vehicle-data/insurance-data-state.service';
import {
  VehicleStateStateService
} from '../../../../core/services/impl/section-services/vehicle-data/vehicle-state-state.service';
import { DeviceService } from '../../../services/device.service';
import { VehicleQuestionModel } from '../../../../core/models/vehicle-question.model';
import { VehicleUseSectionConfig } from './vehicle-use-section.config';
import { Validators } from '@angular/forms';
import { AboardImportedEnum } from '../../../../core/consts/aboard-imported.enum';
import {isCarOne, isKIOSKPOLIS, isNestBank, isNext} from '../../../../core/utils/utils';
import {isNotNullOrUndefined} from 'codelyzer/util/isNotNullOrUndefined';
import {VehicleAbroadImported} from '../../../../core/consts/vehicle-abroad-imported.enum';
import {MaterialDatepickerComponent} from '../../../components/fields/datepickers/material-datepicker/material-datepicker.component';
import {Subscription} from 'rxjs/Rx';
import {FieldsVisibilityService} from '../../../../core/services/fields-visibility.service';
import {OwnerDataConsts} from '../../../../core/consts/owner-data.consts';
import {isYearInLastNYears} from '../../../utils/time-utils';
import {getDrivingLicenseIssueMonths} from '../../../utils/driving-license-years';

@Component({
  selector: 'app-vehicle-use-section',
  templateUrl: './vehicle-use-section.component.html'
})
export class VehicleUseSectionComponent extends SectionBase implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('firstRegistrationDateField') firstRegistrationDateField: MaterialDatepickerComponent;
  @ViewChild('purchaseYearField') purchaseYearField: MaterialDatepickerComponent;

  readonly LAST_YEARS_TO_SHOW_PURCHASE_MONTH = 1;

  public config: VehicleUseSectionConfig;
  public consts = {
    ...VehicleDataEnum,
    ...InsuranceDataEnum,
    ...VehicleStateEnum,
    ...VehicleModelConsts
  };
  public vehicleCondition = VehicleCondition;
  public dataPresentationFields: any[];
  public purchaseMonthCollected: boolean;

  sectionClass = 'vehicle-use-section';
  isNext = isNext();
  verifyDataVisible = isKIOSKPOLIS() || isNestBank();
  threeColumnsLayout: boolean = false;

  private subscriptions: Subscription[] = [];

  get isVehicleDamaged(): boolean {
    return this.getControl(this.consts.VEHICLE_CONDITION).value === VehicleCondition.DAMAGED;
  }

  get isVehicleAbroadImportedNo(): boolean {
    return this.getControl(this.consts.ABROAD_IMPORTED).value === AboardImportedEnum.ABROAD_IMPORTED_NO;
  }

  get damageDescriptionAllowed(): boolean {
    return this.vehicleStateStateService.damageDescriptionEnabled && this.canFieldDisplayed(this.consts.DAMAGE_DESCRIPTION) && this.vehicleStateStateService.vehicleState.vehicleCondition === VehicleCondition.DAMAGED;
  }

  get inlineDamageDescriptionVisible(): boolean {
    return (isKIOSKPOLIS() || isNestBank()) && this.damageDescriptionAllowed;
  }

  get nonInlineDamageDescriptionVisible(): boolean {
    return isCarOne() && this.damageDescriptionAllowed;
  }

  get abroadImportedTimeVisible(): boolean {
    return this.isNext && this.getControl(this.consts.ABROAD_IMPORTED).value === true;
  }

  get purchaseMonthFieldVisible(): boolean {
    return this.purchaseMonthCollected && this.isFieldVisible(this.consts.PURCHASE_MONTH);
  }

  constructor(public vehicleDataStateService: VehicleDataStateService,
              private predefined: PredefinedValuesService,
              private predefinedState: PredefinedValuesStateService,
              private insuranceDataStateService: InsuranceDataStateService,
              public vehicleStateStateService: VehicleStateStateService,
              public deviceService: DeviceService,
              private fieldsVisibilityService: FieldsVisibilityService) {
    super();
    this.config = new VehicleUseSectionConfig(vehicleDataStateService, predefined, insuranceDataStateService, vehicleStateStateService);
  }

  ngOnInit() {
    this.initBean();
    this.initIsFieldDirtyBean();
    this.createConfig();
    this.buildForm();
    this.initFieldsBehaviour();
    this.initControlsChanges();
    this.prepareFieldsInPreviewMode();
    this.handleProductionYearChange();

    if (isNotNullOrUndefined(this.vehicleStateStateService.vehicleState.vehicleCondition)) {
      this.updateDamageDescriptionValidators(<VehicleCondition>this.vehicleStateStateService.vehicleState.vehicleCondition);
    }
  }

  ngAfterViewInit() {
    this.updatePurchaseYearState();
    this.updatePurchaseMonthState();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
    this.subscriptions = [];
  }

  initBean() {
    this.bean = {
      ...this.vehicleDataStateService.vehicleData,
      ...this.insuranceDataStateService.insuranceData,
      ...this.vehicleStateStateService.vehicleState,
      questions: this.mergeQuestions()
    };

    if (this.isNext) {
      if (isNotNullOrUndefined(this.vehicleStateStateService.vehicleState.abroadImported) && this.vehicleStateStateService.vehicleState.abroadImported !== VehicleAbroadImported.ABROAD_IMPORTED_NO) {
        this.bean.abroadImported = true;
        this.bean.abroadImportedTime = this.vehicleStateStateService.vehicleState.abroadImported;
      }
    }
  }

  createConfig() {
    this.formConfig = this.config.createVehicleUseConfig();
  }

  initControlsChanges() {
    const controlsChanges = [
      {controlName: this.consts.IS_LEASING, behaviour: value => this.vehicleStateStateService.changeIsLeasing(value)},
      // {controlName: VehicleDataConsts.VEHICLE_TYPE, behaviour: value => this.stateService.changeVehicleType(value)},
      {
        controlName: this.consts.PARKING_PLACE,
        behaviour: value => this.vehicleStateStateService.changeParkingPlace(value)
      },
      {
        controlName: this.consts.COUNTRY_OF_ORIGIN,
        behaviour: value => this.vehicleStateStateService.changeCountryOfOrigin(value)
      },
      {
        controlName: this.consts.IS_LEFT_HAND_DRIVE,
        behaviour: value => this.vehicleDataStateService.changeIsLeftHandDrive(value)
      },
      {
        controlName: this.consts.ACTUAL_MILEAGE,
        behaviour: value => this.vehicleStateStateService.changeActualMileage(value)
      },
      {
        controlName: this.consts.IS_GAS_INSTALLATION,
        behaviour: value => this.vehicleDataStateService.changeIsGasInstallation(value)
      },
      {
        controlName: this.consts.IS_SERVICE_HISTORY,
        behaviour: value => this.vehicleDataStateService.changeIsServiceHistory(value)
      },
      {controlName: this.consts.IS_ABROAD, behaviour: value => this.vehicleStateStateService.changeIsAbroad(value)},
      // {
      //   controlName: this.consts.YOUNGEST_DRIVER_AGE,
      //   behaviour: value => this.insuranceDataStateService.changeYoungestDriverAge(value)
      // },
      {
        controlName: this.consts.AVG_MILEAGE_PER_YEAR,
        behaviour: value => this.vehicleStateStateService.changeAvgMileagePerYear(value)
      },
      {
        controlName: this.consts.IS_VALID_CAR_INSPECTION,
        behaviour: value => this.vehicleStateStateService.changeIsValidCarInspection(value)
      },
      {controlName: this.consts.USAGE_TYPE, behaviour: value => this.vehicleStateStateService.changeUsageType(value)},
      {
        controlName: this.consts.FIRST_REGISTRATION_DATE,
        behaviour: value => this.vehicleDataStateService.changeFirstRegistrationDate(value)
      },
      {
        controlName: this.consts.ABROAD_IMPORTED,
        behaviour: value => {
          this.updateCountryOfOriginValueAndValidators(value);
          if (this.isNext) {
            if (value === VehicleAbroadImported.ABROAD_IMPORTED_NO) {
              this.vehicleStateStateService.changeAbroadImported(value);
            } else {
              this.getControl(this.consts.ABROAD_IMPORTED_TIME).setValue(VehicleAbroadImported.ABROAD_IMPORTED_OVER_12_MONTHS);
            }
          } else {
            this.vehicleStateStateService.changeAbroadImported(value);
          }
        }
      },
      {
        controlName: this.consts.VEHICLE_CONDITION, behaviour: value => {
          this.updateDamageDescriptionValidators(value);
          this.vehicleStateStateService.changeVehicleCondition(value);
        }
      },
      {
        controlName: this.consts.PURCHASE_YEAR,
        behaviour: value => {
          this.vehicleStateStateService.changePurchaseYear(value);
          this.updatePurchaseMonthState();
        }
      },
      {
        controlName: this.consts.PURCHASE_MONTH,
        behaviour: value => this.vehicleStateStateService.changePurchaseMonth(value)
      },
    ];

    if (this.vehicleStateStateService.damageDescriptionEnabled) {
      controlsChanges.push({
        controlName: this.consts.DAMAGE_DESCRIPTION,
        behaviour: value => this.vehicleStateStateService.changeDamageDescription(value)
      });
    }

    if (this.isNext) {
      controlsChanges.push({
        controlName: this.consts.ABROAD_IMPORTED_TIME,
        behaviour: value => {
          this.vehicleStateStateService.changeAbroadImported(value);
        }
      });
    }
    this.handleControlsState(...controlsChanges);
  }

  markAsDirty(event) {
    this.predefinedState.isDirtyBean = event;
  }

  isFieldVisible(fieldKey: string): boolean {
    return this.canFieldDisplayed(fieldKey) && this.fieldsVisibilityService.isFieldVisible('VEHICLE_USE_SECTION', fieldKey);
  }

  get vehicleCountryOfOriginFieldVisible(): boolean {
    return this.canFieldDisplayed(this.consts.COUNTRY_OF_ORIGIN) && this.vehicleStateStateService.vehicleCountryOfOriginVisible;
  }

  private initIsFieldDirtyBean() {
    this.isDirtyBean = this.predefinedState.isDirtyBean;
  }

  private mergeQuestions(): VehicleQuestionModel[] {
    const vehicleDataQuestions = this.vehicleDataStateService.vehicleData.questions;
    const vehicleStateQuestions = this.vehicleStateStateService.vehicleState.questions;
    return [...vehicleDataQuestions, ...vehicleStateQuestions];
  }

  private updateDamageDescriptionValidators(vehicleCondition: VehicleCondition): void {
    if (this.vehicleStateStateService.damageDescriptionEnabled) {
      if (vehicleCondition === VehicleCondition.DAMAGED) {
        this.getControl(this.consts.DAMAGE_DESCRIPTION).setValidators([Validators.required, Validators.maxLength(1000)]);
      } else {
        this.getControl(this.consts.DAMAGE_DESCRIPTION).reset();
        this.getControl(this.consts.DAMAGE_DESCRIPTION).clearValidators();
      }
      this.getControl(this.consts.DAMAGE_DESCRIPTION).updateValueAndValidity();
    }
  }

  private updateCountryOfOriginValueAndValidators(abroadImported: string): void {
    if (abroadImported !== AboardImportedEnum.ABROAD_IMPORTED_NO) {
      this.getControl(this.consts.COUNTRY_OF_ORIGIN).setValidators([Validators.required]);
    } else {
      this.getControl(this.consts.COUNTRY_OF_ORIGIN).reset();
      this.getControl(this.consts.COUNTRY_OF_ORIGIN).clearValidators();
    }
    this.getControl(this.consts.COUNTRY_OF_ORIGIN).updateValueAndValidity();
  }

  private prepareFieldsInPreviewMode(): void {
    this.dataPresentationFields = [
      this.consts.PARKING_PLACE,
      this.consts.USAGE_TYPE,
      this.consts.AVG_MILEAGE_PER_YEAR,
      this.consts.IS_ABROAD,
      this.consts.ACTUAL_MILEAGE,
      this.consts.IS_LEFT_HAND_DRIVE,
      this.consts.IS_GAS_INSTALLATION,
      this.consts.IS_VALID_CAR_INSPECTION,
      this.consts.IS_LEASING,
      this.consts.VEHICLE_CONDITION,
      this.consts.FIRST_REGISTRATION_DATE,
      this.consts.ABROAD_IMPORTED,
      this.consts.REGISTRATION_NUMBER
    ];

    if (this.vehicleStateStateService.damageDescriptionEnabled && this.isVehicleDamaged) {
      this.dataPresentationFields.push(this.consts.DAMAGE_DESCRIPTION);
    }
    if (!this.isVehicleAbroadImportedNo) {
      this.dataPresentationFields.push(this.consts.COUNTRY_OF_ORIGIN);
    }
  }

  private initFieldsBehaviour(): void {
    this.updateCountryOfOriginValueAndValidators(this.getControl(this.consts.ABROAD_IMPORTED).value)
  }

  private handleProductionYearChange(): void {
    this.subscriptions.push(this.vehicleDataStateService.productionDateChange().subscribe(year => {
      this.getFieldConfig(this.consts.FIRST_REGISTRATION_DATE).materialDateFieldConfig.startAt = () => this.vehicleDataStateService.getProductionDate();
      this.getFieldConfig(this.consts.FIRST_REGISTRATION_DATE).materialDateFieldConfig.maxDate = this.vehicleDataStateService.getMaxFirstRegistrationDate();
      if (this.firstRegistrationDateField) {
        this.firstRegistrationDateField.setStartYear();
        this.firstRegistrationDateField.initDateRange();
        this.firstRegistrationDateField.reset();
      }

      this.updatePurchaseYearState();
    }));
  }

  private updatePurchaseYearState(): void {
    if (isNotNullOrUndefined(this.vehicleDataStateService.getProductionDate())) {
      this.getFieldConfig(this.consts.PURCHASE_YEAR).materialDateFieldConfig.minDate = this.vehicleDataStateService.getProductionDate() + '';
      if (this.purchaseYearField) {
        this.purchaseYearField.setStartYear();
        this.purchaseYearField.initDateRange();
        if (this.vehicleStateStateService.vehicleState.purchaseYear < this.vehicleDataStateService.getProductionDate()) {
          this.purchaseYearField.reset();
        }
        this.updatePurchaseMonthState();
      }
    }
  }

  private updatePurchaseMonthState(): void {
    const { purchaseYear } = this.vehicleStateStateService.vehicleState;
    if (isNotNullOrUndefined(purchaseYear)) {
      this.purchaseMonthCollected = isYearInLastNYears(purchaseYear, this.LAST_YEARS_TO_SHOW_PURCHASE_MONTH);
    } else {
      this.purchaseMonthCollected = false;
    }

    if (this.purchaseMonthCollected) {
      const items = this.getPurchaseMonths(purchaseYear);
      this.getFieldConfig(this.consts.PURCHASE_MONTH).options['items'] = items;
      this.getControl(this.consts.PURCHASE_MONTH).setValidators([Validators.required]);

      // validate existing value
      const month = this.getControl(this.consts.PURCHASE_MONTH).value;
      if (isNotNullOrUndefined(month) && !items.find(item => item.key === month)) { // month is defined but it doesn't exist in current dictionary
        this.getControl(this.consts.PURCHASE_MONTH).reset();
      }
    } else {
      this.getControl(this.consts.PURCHASE_MONTH).reset();
      this.getControl(this.consts.PURCHASE_MONTH).clearValidators();
    }
    this.getControl(this.consts.PURCHASE_MONTH).updateValueAndValidity();
  }

  private getPurchaseMonths(purchaseYear: number): any[] {
    const fullDictionary = [
      { key: 1, label: 'Styczeń' },
      { key: 2, label: 'Luty' },
      { key: 3, label: 'Marzec' },
      { key: 4, label: 'Kwiecień' },
      { key: 5, label: 'Maj' },
      { key: 6, label: 'Czerwiec' },
      { key: 7, label: 'Lipiec' },
      { key: 8, label: 'Sierpień' },
      { key: 9, label: 'Wrzesień' },
      { key: 10, label: 'Październik' },
      { key: 11, label: 'Listopad' },
      { key: 12, label: 'Grudzień' },
    ];

    const today = new Date();
    if (today.getFullYear() === purchaseYear) {
      return fullDictionary.filter(item => item.key <= today.getMonth() + 1);
    } else {
      return fullDictionary;
    }
  }
}

